"use client";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  Button,
  Container,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Link,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Send,
  Business,
  Message,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import Header from "../components/HomepageContent/Header";
import Footer from "../components/HomepageContent/Footer";
import WarningModal from "@/components/WarningModal";
import authenticationApi from "@/lib/authenticationApi";

type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  message: string;
};

function formatUSPhone(raw: string): string {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": {
      borderColor: "rgba(21,60,127,0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(21,60,127,0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#153C7F",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#153C7F",
  },
};

export default function Contact() {
  useEffect(() => {
    document.title = "CareAscendium - Contact";
  }, []);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      message: "",
    },
  });

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      await authenticationApi.post("/v1/contact/send", {
        p_first_name: data.firstName.trim(),
        p_last_name: data.lastName.trim(),
        p_email: data.email.trim(),
        p_phone: data.phone.trim(),
        p_company_name: data.companyName.trim(),
        p_message: data.message.trim(),
      });
      reset();
      setModalOpen(true);
    } catch (err: unknown) {
      const e = err as {
        details?: { message?: string; error_message?: string };
        message?: string;
      };
      const errorMessage =
        e?.details?.message ||
        e?.details?.error_message ||
        e?.message ||
        "Failed to send message. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafafa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)",
          py: 12,
          px: 4,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)",
            backgroundSize: "50px 50px",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h1"
            sx={{
              color: "white",
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 6,
              color: "rgba(255,255,255,0.9)",
              maxWidth: "900px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: "1.2rem", md: "1.5rem" },
            }}
          >
            Ready to transform your skilled nursing facility? Let's discuss how
            CareAscendium can help you achieve your goals.
          </Typography>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              id="contact-form"
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                border: "1px solid rgba(21,60,127,0.1)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    background: "linear-gradient(45deg, #153C7F, #1E4A8C)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 3,
                    boxShadow: "0 4px 15px rgba(21,60,127,0.3)",
                  }}
                >
                  <Message sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Typography
                  variant="h3"
                  sx={{ color: "#153C7F", fontWeight: 700 }}
                >
                  Send us a Message
                </Typography>
              </Box>

              <Box
                component="form"
                sx={{ mt: 3 }}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{
                        required: "First Name is required",
                        validate: (v) => {
                          const trimmed = (v || "").trim();
                          if (!trimmed) return "First Name is required";
                          if (!/^[a-zA-Z\s\-'.]+$/.test(trimmed))
                            return "First Name should contain only letters";
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          type="text"
                          label="First Name"
                          variant="outlined"
                          autoComplete="given-name"
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/[0-9]/g, ""))
                          }
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "text",
                            "aria-label": "First Name",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{
                        required: "Last Name is required",
                        validate: (v) => {
                          const trimmed = (v || "").trim();
                          if (!trimmed) return "Last Name is required";
                          if (!/^[a-zA-Z\s\-'.]+$/.test(trimmed))
                            return "Last Name should contain only letters";
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          type="text"
                          label="Last Name"
                          variant="outlined"
                          autoComplete="family-name"
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/[0-9]/g, ""))
                          }
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "text",
                            "aria-label": "Last Name",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      variant="outlined"
                      autoComplete="email"
                      {...register("email", {
                        required: "Email Address is required",
                        validate: (v) =>
                          v?.trim() ? true : "Email Address is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email format",
                        },
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Phone Number"
                          variant="outlined"
                          type="tel"
                          placeholder="(555) 123-4567"
                          onChange={(e) =>
                            field.onChange(formatUSPhone(e.target.value))
                          }
                          error={!!errors.phone}
                          helperText={errors.phone?.message}
                          sx={textFieldSx}
                          inputProps={{
                            inputMode: "tel",
                            "aria-label": "Phone Number",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      type="text"
                      label="Company/Facility Name"
                      variant="outlined"
                      autoComplete="organization"
                      {...register("companyName")}
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      required
                      label="Message"
                      multiline
                      rows={4}
                      variant="outlined"
                      {...register("message", {
                        required: "Message is required",
                        validate: (v) =>
                          v?.trim() ? true : "Message is required",
                      })}
                      error={!!errors.message}
                      helperText={errors.message?.message}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Send />
                    )
                  }
                  sx={{
                    mt: 4,
                    py: 2,
                    background: "linear-gradient(45deg, #153C7F, #1E4A8C)",
                    color: "white",
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 4px 15px rgba(21,60,127,0.3)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #0F2A5A, #153C7F)",
                      boxShadow: "0 6px 20px rgba(21,60,127,0.4)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  SUBMIT
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ height: "100%" }}>
              {/* Direct Contact */}
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid rgba(21,60,127,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(21,60,127,0.15)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      background: "linear-gradient(45deg, #153C7F, #1E4A8C)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 3,
                      boxShadow: "0 4px 15px rgba(21,60,127,0.3)",
                    }}
                  >
                    <Person sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ color: "#153C7F", fontWeight: 700 }}
                  >
                    Direct Contact
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: "#153C7F", fontWeight: 700, mb: 0.5 }}
                  >
                    Bharanth Bynagari
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#666", mb: 2 }}>
                    Founder & CEO
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Email sx={{ fontSize: 20, color: "#153C7F", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#666" }}>
                    <Link
                      href="mailto:bynagari@careascendium.com"
                      sx={{
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      bynagari@careascendium.com
                    </Link>
                  </Typography>
                </Box>

                {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 20, color: '#153C7F', mr: 2 }} />
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    Mobile: 317-590-8275
                  </Typography>
                </Box> */}
              </Paper>

              {/* Company Information */}
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid rgba(21,60,127,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(21,60,127,0.15)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      background: "linear-gradient(45deg, #00B4D8, #0099CC)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 3,
                      boxShadow: "0 4px 15px rgba(0,180,216,0.3)",
                    }}
                  >
                    <Business sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "#153C7F", fontWeight: 700 }}
                  >
                    Company Information
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{ color: "#666", mb: 3, lineHeight: 1.6 }}
                >
                  CareAscendium, Inc. is dedicated to transforming skilled
                  nursing facility operations through innovative technology
                  solutions.
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <LocationOn sx={{ fontSize: 20, color: "#153C7F", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#666" }}>
                    Carmel, IN
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Business sx={{ fontSize: 20, color: "#153C7F", mr: 2 }} />
                  <Typography variant="body1" sx={{ color: "#666" }}>
                    CareAscendium, Inc.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      {/* <Box
        sx={{
          py: 12,
          background: "linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)",
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage:
              "radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)",
            backgroundSize: "50px 50px",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}
          >
            Schedule a personalized demonstration and see how CareAscendium can
            transform your facility operations
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<Send />}
            onClick={() => {
              // Scroll to the contact form
              document
                .getElementById("contact-form")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            sx={{
              bgcolor: "white",
              color: "#153C7F",
              px: 6,
              py: 3,
              fontSize: "1.2rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(255,255,255,0.3)",
              "&:hover": {
                bgcolor: "#f5f5f5",
                boxShadow: "0 6px 20px rgba(255,255,255,0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Schedule a Demo
          </Button>
        </Container>
      </Box> */}

      {/* Success Modal */}
      <WarningModal
        open={modalOpen}
        onClose={handleCloseModal}
        type="success"
        title="Message Sent Successfully!"
        message="Thank you for your interest! We will contact you within 24 hours to schedule your demo."
        primaryButtonText="OK"
        onPrimary={handleCloseModal}
      />
      <Footer />
    </Box>
  );
}
